import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Clock, Pencil, Trash2, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/components/music/MusicWidget";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { NutritionPanel } from "@/components/recipes/NutritionPanel";
import { IngredientList, NoAiNote, Scaler, ScaleTips, Substitutes } from "@/components/recipes/RecipeParts";

const mins = (m) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`);

const Meta = ({ r, foodHref }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#A8A090]" data-testid="recipe-meta">
    {r.prep_minutes != null && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Prep {mins(r.prep_minutes)}</span>}
    {r.cook_minutes != null && <span>Cook {mins(r.cook_minutes)}</span>}
    {r.prep_minutes != null && r.cook_minutes != null && <span className="text-[#F3EBDD]">Total {mins(r.prep_minutes + r.cook_minutes)}</span>}
    {r.universe_name !== undefined && <span>{r.universe_name || "My kitchen"}</span>}
    {foodHref && <Link to={foodHref} className="inline-flex items-center gap-1 text-[#FB923C] hover:underline" data-testid="recipe-food-note-link"><UtensilsCrossed className="h-3.5 w-3.5" />Food note</Link>}
    {(r.tags || []).map((t) => <span key={t} className="rounded-full border border-[#2D3442] px-2 py-0.5 text-[11px]">{t}</span>)}
  </div>
);

export const RecipeView = ({ recipe: r, canEdit, back, backLabel, onEdit, onDeleted }) => {
  const { user } = useAuth();
  const paid = r.ai_tools ?? isPaid(user);
  const [servings, setServings] = useState(r.servings);
  const [tips, setTips] = useState(null);
  const [subs, setSubs] = useState(null);
  const [del, setDel] = useState(false);
  const adjusted = tips && tips.servings === servings ? Object.fromEntries(tips.adjusted.map((a) => [a.id, a])) : null;
  return (
    <article className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="recipe-view">
      <div className="flex items-center gap-3">
        <button onClick={back} className="inline-flex items-center gap-1 text-sm text-[#A8A090] hover:text-[#F3EBDD]" data-testid="recipe-back"><ChevronLeft className="h-4 w-4" />{backLabel}</button>
        <span className="flex-1" />
        {canEdit && <><Button size="sm" variant="outline" onClick={onEdit} data-testid="recipe-edit-button"><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => setDel(true)} className="text-[#F87171]" data-testid="recipe-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button></>}
      </div>
      <header className="space-y-3">
        <div className="lf-label">Recipe · serves {r.servings}</div>
        <h1 className="font-display text-4xl leading-tight text-[#F3EBDD] sm:text-5xl" data-testid="recipe-title">{r.title}</h1>
        {r.description && <p className="max-w-3xl whitespace-pre-wrap text-base leading-relaxed text-[#D6CCBC]">{r.description}</p>}
        <Meta r={r} foodHref={r.entity_id && r.universe_id ? `/u/${r.universe_id}/lore/${r.entity_id}` : null} />
      </header>
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-8">
          <section className="space-y-4">
            <h2 className="font-display text-2xl text-[#F3EBDD]">Ingredients</h2>
            <Scaler base={r.servings} value={servings} onChange={setServings} />
            <IngredientList recipe={r} factor={servings / r.servings} adjusted={adjusted} subs={subs} />
            {!r.ingredients?.length && <p className="text-sm text-[#7A7468]">No ingredients yet.</p>}
            <ScaleTips recipe={r} servings={servings} paid={paid} tips={tips} setTips={setTips} />
            <Substitutes recipe={r} paid={paid} subs={subs} setSubs={setSubs} />
          </section>
          {r.steps?.length > 0 && <section className="space-y-3"><h2 className="font-display text-2xl text-[#F3EBDD]">Method</h2>
            <ol className="space-y-4">{r.steps.map((s, i) => <li key={i} className="flex gap-4" data-testid={`recipe-step-view-${i}`}><span className="font-display text-2xl leading-none text-[#F59E0B]">{i + 1}</span><p className="whitespace-pre-wrap text-[15px] leading-relaxed text-[#E8DFD1]">{s}</p></li>)}</ol></section>}
          {r.notes && <section className="space-y-2"><h2 className="font-display text-2xl text-[#F3EBDD]">Notes</h2><p className="whitespace-pre-wrap text-sm leading-relaxed text-[#D6CCBC]">{r.notes}</p></section>}
        </div>
        <aside className="space-y-3"><NutritionPanel recipe={r} paid={paid} servings={servings} /><NoAiNote /></aside>
      </div>
      <ConfirmDialog open={del} onOpenChange={setDel} title="Delete this recipe?" description="This can't be undone."
        onConfirm={async () => { await api.delete(`/recipes/${r.id}`); toast.success("Recipe deleted"); onDeleted(); }} />
    </article>
  );
};
